import React, { useState } from 'react'
import { ProjectsTableLineProps } from './ProjectsTableLine.types'
import styles from './ProjectsTableLine.module.scss'
import cl from 'classnames'
import { Button, ExpandButton, SwitchButton, TableBodyLine, TableTagsCell, Text1 } from 'components/shared'
import { projectsTableWidths } from '../ProjectsTable.types'
import { WarningMessage } from 'components'
import { inject } from 'mobx-react'
import { GENERAL_STORE, ProjectsTableSortProps, PROJECTS_TABLE_STORE, PROJECT_STORE, ROUTING_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import { renderProjectStatusComponent } from './ProjectsTableLine.utils'
import { Project, StatusTypes } from 'models'
import { IconBin, IconEditor } from 'assets'
import { formatNumberShort, isProjectPublished } from 'utilities'

const ProjectsTableLineComponent = (props: ProjectsTableLineProps) => {
  const {
    project,
    expandLevel = 0,
    [PROJECTS_TABLE_STORE]: {
      isColumnMarked,
      deleteProjectById
    },
    [PROJECT_STORE]: {
      changeStatus
    },
    [ROUTING_STORE]: {
      push
    },
    [GENERAL_STORE]: {
      confirmAction
    },
    isMyProfileRender
  } = props

  const isExpandable = Boolean(project.children && project.children.length)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleStatusSwitchButtonClick = (project: Project) => {
    const newStatus = isProjectPublished(project.status)
      ? StatusTypes.NOT_PUBLISHED
      : StatusTypes.PUBLISHED

    const title = {
      [StatusTypes.NOT_PUBLISHED]: 'Unpublish this project',
      [StatusTypes.PUBLISHED]: 'Publish this project'
    }[newStatus]
    const description = {
      [StatusTypes.NOT_PUBLISHED]: 'This will unpublish the project and make it disappear from the player. Are you sure?',
      [StatusTypes.PUBLISHED]: 'This will publish the project and make it visible in the player. Are you sure?'
    }[newStatus]
    const submitText = {
      [StatusTypes.NOT_PUBLISHED]: 'Unpublish',
      [StatusTypes.PUBLISHED]: 'Publish'
    }[newStatus]

    confirmAction({
      title,
      description,
      project,
      submitText,
      onSubmit: () => {
        changeStatus(project.id, newStatus)
      }
    })
  }

  const handleDeleteProject = (projectId: string | number) => {
    confirmAction({
      title: 'Delete this project',
      description: 'This will delete the project and all its content. Are you sure?',
      project,
      submitText: 'Delete',
      onSubmit: () => {
        // TODO: delete project
        deleteProjectById(projectId)
      }
    })
  }

  return (
    <div className={cl(styles.container, {
      [cl(styles.expandedTopRight, styles.expandedBottomLeft)]:
        isExpanded && expandLevel === 0
    })}
    >
      <TableBodyLine
        cells={[
          {
            // widthPercentage: projectsTableWidths.name,
            isColumnMarked: isColumnMarked(ProjectsTableSortProps.NAME),
            children: (
              <>
                {isExpandable && (
                  <ExpandButton
                    isExpanded={isExpanded}
                    onClick={() => setIsExpanded(s => !s)}
                    style={{ marginLeft: expandLevel * 20 }}
                  />
                )}
                <Text1
                  bold
                  overflowEllipsis
                  style={
                    !isExpandable && expandLevel
                      ? { marginLeft: 24 + expandLevel * 10 }
                      : undefined
                  }
                  className={styles.clickable}
                  onClick={() => push(`/projects/edit/${project.id}`)}
                >
                  {project.name}
                </Text1>
                <WarningMessage
                  object={project}
                  objectType='project'
                />
              </>
            ),
            widthPx: projectsTableWidths.name
          },
          {
            // widthPercentage: projectsTableWidths.type,
            isColumnMarked: isColumnMarked(ProjectsTableSortProps.TYPE),
            children: <Text1 overflowEllipsis>{project.type}</Text1>,
            widthPx: projectsTableWidths.type
          },
          {
            // widthPercentage: projectsTableWidths.addedAt,
            isColumnMarked: isColumnMarked(ProjectsTableSortProps.ADDED_AT),
            children: <Text1 overflowEllipsis>{project.addedAtAsString}</Text1>,
            widthPx: projectsTableWidths.addedAt
          },
          {
            // widthPercentage: projectsTableWidths.tags,
            children: <TableTagsCell tags={project.tags} />,
            widthPx: projectsTableWidths.tags
          },
          {
            isColumnMarked: isColumnMarked(ProjectsTableSortProps.INPUT_TOKEN_USED),
            children: <Text1 overflowEllipsis>{formatNumberShort(project.inputGeminiTokens || 0)}</Text1>,
            widthPx: projectsTableWidths.inputTokenUsed
          },
          {
            isColumnMarked: isColumnMarked(ProjectsTableSortProps.OUTPUT_TOKEN_USED),
            children: <Text1 overflowEllipsis>{formatNumberShort(project.outputGeminiTokens || 0)}</Text1>,
            widthPx: projectsTableWidths.outputTokenUsed
          },
          {
            // widthPercentage: projectsTableWidths.genre,
            isColumnMarked: isColumnMarked(ProjectsTableSortProps.GENRE),
            children: (
              <Text1 overflowEllipsis title={project.genresAsString}>
                {project.genresAsString}
              </Text1>
            ),
            widthPx: projectsTableWidths.genre
          },
          {
            // widthPercentage: projectsTableWidths.status,
            children: renderProjectStatusComponent(project),
            widthPx: projectsTableWidths.status
          },
          {
            // widthPercentage: projectsTableWidths.published,
            children: (
              <SwitchButton
                style={{ marginLeft: 12 }}
                isOn={
                  project.status !== StatusTypes.NOT_PUBLISHED &&
                  project.status !== StatusTypes.NEW
                }
                alwaysSync
                onChange={() => handleStatusSwitchButtonClick(project)}
              />
            ),
            widthPx: isMyProfileRender ? 97 : projectsTableWidths.published
          },
          {
            // widthPercentage: projectsTableWidths.openInEditor,
            children: (
              <Button
                preset='icon'
                style={{ marginLeft: 'auto' }}
                onClick={() => push(`/editor/${project.id}`)}
              >
                <IconEditor fill={styles.secondaryContent} />
              </Button>
            ),
            widthPx: projectsTableWidths.openInEditor
          },
          {
            // widthPercentage: projectsTableWidths.deleteProject,
            children: (
              <Button
                preset='icon'
                style={{ marginLeft: 'auto' }}
                disabled={project.status === StatusTypes.PUBLISHED || project.status === StatusTypes.HAS_CHANGES }
                onClick={() => handleDeleteProject(project.id)}
              >
                <IconBin fill={styles.secondaryContent} />
              </Button>
            ),
            widthPx: projectsTableWidths.deleteProject
          }
        ]}
      />
      {isExpanded && (
        project.children.map(p => (
          <ProjectsTableLine key={p.id} project={p} expandLevel={expandLevel + 1} />
        ))
      )}
    </div>
  )
}

const ProjectsTableLine = inject(
  PROJECTS_TABLE_STORE, ROUTING_STORE, GENERAL_STORE, PROJECT_STORE
)(observer(ProjectsTableLineComponent))
export { ProjectsTableLine }
